import { LoadingSpinner } from "@/components/loading-spinner";
import { H2 } from "@/components/typography";
import { adTypeMap } from "@/http/api-enums";
import { CalibrationPlotsResponse } from "@/http/api-types";
import { useState } from "react";
import CalibrationPlotsChart from "./calibration-plots-chart";

// Import the chartConfig for consistency
const chartConfig = {
  // Line metrics (Y-axis)
  AvgEstimatedIpm: {
    label: "Predicted Install Rate (IPM)",
    color: "hsl(var(--chart-1))",
  },
  ApIpm: {
    label: "Actual Install Rate (IPM)",
    color: "hsl(var(--chart-2))",
  },
  AvgEstimatedWinRate: {
    label: "Predicted Win Rate",
    color: "hsl(var(--chart-3))",
  },
  WinRate: {
    label: "Actual Win Rate",
    color: "hsl(var(--chart-4))",
  },
  ImpPerBids: {
    label: "Impressions per Bid",
    color: "hsl(var(--chart-5))",
  },
  AvgBidPriceCpmMicroEur: {
    label: "Avg Bid Price (EUR CPM)",
    color: "hsl(var(--chart-1))",
  },
  PredictedCpiEur: {
    label: "Predicted CPI (EUR)",
    color: "hsl(var(--chart-2))",
  },
  ActualCpiEur: {
    label: "Actual CPI (EUR)",
    color: "hsl(var(--chart-3))",
  },

  // Bar metrics (summed values)
  RevenueEur: {
    label: "Revenue (EUR)",
    color: "hsl(var(--chart-1))",
  },
  TotalBids: {
    label: "Total Bids",
    color: "hsl(var(--chart-2))",
  },
  TotalImpressions: {
    label: "Total Impressions",
    color: "hsl(var(--chart-3))",
  },
  TotalApConversions: {
    label: "Total AP Conversions",
    color: "hsl(var(--chart-4))",
  },
};

type CalibrationPlotsProps = {
  data?: CalibrationPlotsResponse;
  error?: any;
  isMutating?: boolean;
};

const decimalFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  maximumSignificantDigits: 3,
  notation: "compact",
  compactDisplay: "short",
});

function CalibrationPlots({ data, error, isMutating }: CalibrationPlotsProps): JSX.Element {
  // State to manage which lines are visible
  const [visibleLines, setVisibleLines] = useState({
    AvgEstimatedIpm: true,
    ApIpm: true,
    AvgEstimatedWinRate: true,
    WinRate: true,
    ImpPerBids: false, // Start with some lines hidden to avoid clutter
    AvgBidPriceCpmMicroEur: false,
    PredictedCpiEur: false,
    ActualCpiEur: false,
  });

  // State to manage which bar is visible (only one at a time)
  const [visibleBar, setVisibleBar] = useState<keyof typeof chartConfig>("RevenueEur");

  // Toggle line visibility
  const toggleLine = (lineKey: keyof typeof visibleLines) => {
    setVisibleLines(prev => ({
      ...prev,
      [lineKey]: !prev[lineKey],
    }));
  };

  // Handle bar metric selection
  const handleBarChange = (barKey: keyof typeof chartConfig) => {
    if (["RevenueEur", "TotalBids", "TotalImpressions", "TotalApConversions"].includes(barKey)) {
      setVisibleBar(barKey);
    }
  };
  if (isMutating) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div>Error: {JSON.stringify(error.serverError)}</div>;
  }

  if (!data) {
    return <p className="text-muted-foreground">Run the analysis above.</p>;
  }

  if (!data.results || data.results.length === 0) {
    return <p>There is no data for the selected filters. Try again with other combinations</p>;
  }

  // Group data by ad_type for separate charts
  const groupedData = data.results.reduce(
    (acc, row) => {
      if (!acc[row.AdType]) {
        acc[row.AdType] = [];
      }
      acc[row.AdType].push(row);
      return acc;
    },
    {} as Record<string, typeof data.results>,
  );

  return (
    <>
      {data.meta && (
        <p>
          Query completed in {decimalFormatter.format(data.meta.Took / 1e9)} seconds, processed{" "}
          {decimalFormatter.format(data.meta.TotalBytesProcessed / 1e9)} GB which cost{" "}
          {usdFormatter.format(data.meta.CostUSD)}.
        </p>
      )}

      {/* Global chart controls - rendered once */}
      <div className="space-y-4 mb-8">
        {/* Legend for lines with toggle functionality */}
        <div className="flex flex-wrap gap-4 p-4 border rounded-lg bg-muted/10">
          <div className="text-sm font-medium">Line Metrics (toggle to show/hide):</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(visibleLines).map(([key, visible]) => (
              <button
                key={key}
                onClick={() => toggleLine(key as keyof typeof visibleLines)}
                className={`flex items-center gap-2 px-3 py-1 rounded text-xs border transition-all ${
                  visible
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                <div
                  className={`w-3 h-3 rounded-sm ${visible ? "border-0" : "border border-current"}`}
                  style={{
                    backgroundColor: visible ? chartConfig[key as keyof typeof chartConfig]?.color : "transparent",
                  }}
                />
                {chartConfig[key as keyof typeof chartConfig]?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Legend for bar metrics */}
        <div className="flex flex-wrap gap-4 p-4 border rounded-lg bg-muted/10">
          <div className="text-sm font-medium">Bar Metric (select one):</div>
          <div className="flex flex-wrap gap-2">
            {["RevenueEur", "TotalBids", "TotalImpressions", "TotalApConversions"].map(barKey => (
              <button
                key={barKey}
                onClick={() => handleBarChange(barKey as keyof typeof chartConfig)}
                className={`flex items-center gap-2 px-3 py-1 rounded text-xs border transition-all ${
                  visibleBar === barKey
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                <div
                  className={`w-3 h-3 rounded-sm ${visibleBar === barKey ? "border-0" : "border border-current"}`}
                  style={{
                    backgroundColor:
                      visibleBar === barKey ? chartConfig[barKey as keyof typeof chartConfig]?.color : "transparent",
                  }}
                />
                {chartConfig[barKey as keyof typeof chartConfig]?.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {Object.entries(groupedData).map(([adType, adTypeData]) => (
        <div key={adType} className="mb-8">
          <H2>Calibration Plots - {adTypeMap[adType]}</H2>
          <CalibrationPlotsChart data={adTypeData} visibleLines={visibleLines} visibleBar={visibleBar} />
        </div>
      ))}
    </>
  );
}

export default CalibrationPlots;
